import React, {FunctionComponent, useEffect, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useTranslation} from 'react-i18next';
import cn from 'classnames';

import {RunGroupID, TestStatus} from 'enums';
import {ITest, ITestInfo} from 'interfaces';
import {TestItem} from './TestItem';
import { CommandService, TestsService } from 'services';
import { Button } from 'components';

interface IRunGroupProps {
    canChangeSelection: boolean;
    name: string;
    onSelect: () => void;
    selected: boolean;
    tests: ITest[];
    deviceId: number;
}

export const RunGroup: FunctionComponent<IRunGroupProps> = ({canChangeSelection, name, onSelect, selected, tests, deviceId}) => {
    const {t, i18n} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const checkboxColRef = useRef<HTMLLabelElement>();
    const checkboxColClass = 'tests_groupColCheckbox';

    let passCount = tests.filter(t => t.status === TestStatus.Passed).length;
    let failCount = tests.filter(t => t.status === TestStatus.Failed).length;
    let cancelCount = tests.filter(t => t.status === TestStatus.Canceled).length;
    let inProgressCount = tests.filter(t => t.status === TestStatus.InProgress).length;
    let queuedCount = tests.filter(t => t.status === TestStatus.Queued).length;
    let totalRunningCount = passCount + failCount + cancelCount + inProgressCount + queuedCount;

    // Compute the group's status from its tests
    let statusClass = '';
    if (passCount === totalRunningCount && passCount > 0) {
        statusClass = 'passed';
    } else if (failCount > 0 && passCount === 0) {
        statusClass = 'failed';
    } else if (failCount > 0 || cancelCount > 0) {
        statusClass = 'mixed';
    }

    const toggleOpen = (e: React.MouseEvent) => {
        // We expand if the user clicks any part of the group, but we don't want to expand if they click the checkbox column.
        if (checkboxColRef.current && checkboxColRef.current.contains(e.target as HTMLLIElement)) {
            return;
        }
        setIsOpen(!isOpen);
    }

    const t_testsCount = t('tests.groupTestsCount', {testsCount: tests.length})

    const [endButtonDisabled, setEndButtonDisabled] = useState(true);
    const endTest = () => {
        setEndButtonDisabled(true);
        TestsService.endMessageValidation(deviceId)
            .then(() => setEndButtonDisabled(false))
            .catch((err) => {
                //console.error(err);
                setEndButtonDisabled(false);
            });
    }

    const [pingButtonDisabled, setPingButtonDisabled] = useState(true);
    const pingDevice = () => {
        setPingButtonDisabled(true);
        CommandService.pingDevice(deviceId)
            .then(() => setPingButtonDisabled(false))
            .catch((err) => {
                //console.error(err);
                setPingButtonDisabled(false);
            });
    }

    const [commandButtonDisabled, setCommandButtonDisabled] = useState(true);
    const commandDevice = () => {
        setCommandButtonDisabled(true);
        CommandService.commandDevice(deviceId)
            .then(() => setCommandButtonDisabled(false))
            .catch((err) => {
                //console.error(err);
                setCommandButtonDisabled(false);
            });
    }
    
    // if message validation in progress, enable finish test button
    useEffect(() => {
        if (tests[0].status == TestStatus.InProgress) {
            setPingButtonDisabled(false);
            setCommandButtonDisabled(false);
            setEndButtonDisabled(false);
        }
        else {
            setPingButtonDisabled(true);
            setCommandButtonDisabled(true);
            setEndButtonDisabled(true);
        }
    }, [tests[0].status])

    return (<>
        <li 
            className={cn(statusClass, {
                "tests_group": true,
                running: inProgressCount > 0,
            })}
        >
            <label
                className={cn({
                    [checkboxColClass]: true,
                    disabled: !canChangeSelection,
                })}
                ref={checkboxColRef}
            >
                {/* Cast "selected" to boolean so it's passed every time because React ignores undefined */}
                <input
                    type="checkbox"
                    className="tests_groupCheckbox"
                    checked={!!selected}
                    onChange={onSelect}
                    disabled={!canChangeSelection} 
                />
            </label>
            <div className="tests_groupColName ellipsis" title={name + ' ' + t_testsCount}>
                {name}
                <span className="tests_groupTestsCount">{t_testsCount}</span>
            </div>
            <div className="tests_groupColStatus ellipsis">
                {passCount > 0 && (
                    <div className="tests_groupStatusPassed">
                        <FontAwesomeIcon icon="check" className="tests_statusIcon passed" />
                        {t('tests.groupStatuses.passed', { passCount: passCount })}
                    </div>
                )}
                {failCount > 0 && (
                    <div className="tests_groupStatusFailed">
                        <FontAwesomeIcon icon="exclamation-triangle" className="tests_statusIcon failed" />
                        {t('tests.groupStatuses.failed', {failCount: failCount})}
                    </div>
                )}
                {cancelCount > 0 && (
                    <div className="tests_groupStatusCanceled">
                        <FontAwesomeIcon icon="exclamation-triangle" className="tests_statusIcon canceled" />
                        {t('tests.groupStatuses.canceled', {cancelCount: cancelCount})}
                    </div>
                )}
            </div>
            {name.replace(" ", "") == RunGroupID.MessageValidation && tests[0].status == TestStatus.InProgress && <>
                <div className="tests_groupEndButton">
                    <Button onClick={pingDevice} disabled={pingButtonDisabled} className='finish_runGroup_button'>
                        {"Ping"}
                    </Button>
                </div>
                <div className="tests_groupEndButton">
                    <Button onClick={commandDevice} disabled={commandButtonDisabled} className='finish_runGroup_button'>
                        {"Command"}
                    </Button>
                </div>
                <div className="tests_groupEndButton">
                    <Button onClick={endTest} disabled={endButtonDisabled} theme="red" className='finish_runGroup_button'>
                        {"Finish"}
                    </Button>
                </div>
            </>}
            <div className="tests_groupColExpand">
                <button className="tests_expandButton" onClick={toggleOpen} type="button">
                    {isOpen && <FontAwesomeIcon icon="chevron-down" />}
                    {!isOpen && <FontAwesomeIcon icon="chevron-right" />}
                </button>
            </div>
        </li>
        {isOpen && (
            tests.map((test, i) => (
                <TestItem 
                    description={test.description}
                    key={test.id}
                    id={test.id}
                    lastItemInGroup={i === tests.length - 1}
                    name={test.name}
                    resultMessage={test.result}
                    selected={selected}
                    status={test.status}
                />
            ))
        )}
    </>);
}